// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import {
  chtCoolingRateTriggerFpm,
} from "../core/config.js";
import {
  formatElapsedTime,
} from "../core/format.js";
import {
  extentFromValues,
} from "../core/math.js";
import {
  rawMetricDescriptor,
} from "../core/raw-metadata.js";
import {
  cylinderKeys,
} from "../core/schema.js";
import {
  isRotaxEngineContext,
} from "../core/engine-family.js";
import {
  clampScore,
  median,
  peerAverage,
  rollingMedianStep,
  thermalEngineWorkPoint as engineWorkPoint,
} from "./utils.js";
import {
  sensorsParameters,
} from "./detectors/sensors.js";

function labelForSensorKey(key, options = {}) {
  return options.labelForKey?.(key) || rawMetricDescriptor(key)[0];
}

export function sensorChannelType(key) {
  if (String(key).startsWith("rpm")) return "rpm";
  if (String(key).startsWith("map")) return "map";
  if (String(key).startsWith("fuelFlow")) return "fuelFlow";
  if (String(key).startsWith("tit")) return "tit";
  return String(key).startsWith("egt") ? "egt" : "cht";
}

function scalarThermalProbeKeys(data) {
  const keys = [];
  const [titKey, tit1Key, tit2Key] = sensorsParameters.scalarThermalProbeKeys;
  const hasTit = data.some((point) => Number.isFinite(point[titKey]));
  const hasTit1 = data.some((point) => Number.isFinite(point[tit1Key]));
  const hasTit2 = data.some((point) => Number.isFinite(point[tit2Key]));
  if (hasTit1 || hasTit2) {
    if (hasTit1) keys.push(tit1Key);
    if (hasTit2) keys.push(tit2Key);
  } else if (hasTit) {
    keys.push(titKey);
  }
  return keys;
}

function sensorChannelKeys(data) {
  return [
    ...cylinderKeys("egt"),
    ...cylinderKeys("cht"),
    ...scalarThermalProbeKeys(data),
    ...sensorsParameters.rpmKeys,
    ...sensorsParameters.mapKeys,
    ...sensorsParameters.fuelFlowKeys,
  ]
    .filter((key) => data.some((point) => Number.isFinite(point[key])));
}

function sensorPeerKeysForKey(key, allKeys = []) {
  const type = sensorChannelType(key);
  if (type === "rpm") return allKeys.filter((candidate) => candidate !== key && sensorChannelType(candidate) === "rpm");
  if (type === "map") return allKeys.filter((candidate) => candidate !== key && sensorChannelType(candidate) === "map");
  if (type === "fuelFlow") return allKeys.filter((candidate) => candidate !== key && sensorChannelType(candidate) === "fuelFlow");
  if (type === "tit") return allKeys.filter((candidate) => candidate !== key && sensorChannelType(candidate) === "tit");
  return cylinderKeys(type);
}

function sensorRangeForType(type) {
  return sensorsParameters.ranges[type] ?? sensorsParameters.ranges.cht;
}

function engineModelTextForSensorOptions(options = {}) {
  const engine = options.aircraftProfile?.engine ?? {};
  const engines = Array.isArray(engine.engines) ? engine.engines : [];
  return [
    engine.make,
    engine.model,
    options.parserMetadata?.engineMake,
    options.parserMetadata?.engineModel,
    ...engines.flatMap((entry) => [entry?.make, entry?.model]),
  ].map((value) => String(value ?? "").trim()).filter(Boolean).join(" ");
}

function rotaxRpmMaxForOptions(options = {}) {
  if (!isRotaxEngineContext(options)) return null;
  const normalized = engineModelTextForSensorOptions(options).toLowerCase().replace(/[\s_-]+/g, "");
  const limits = sensorsParameters.rotaxRpmLimits;
  if (/912(?:is(?:sport)?|uls)|914(?:ul|is)?|915is?|916is?/.test(normalized)) return limits.highRpmMax;
  if (/(?:^|rotax)912(?:ul)?/.test(normalized)) return limits.plain912Max;
  return null;
}

function sensorRangeForKey(key, options = {}) {
  const type = sensorChannelType(key);
  const range = sensorRangeForType(type);
  if (type !== "rpm") return range;
  const rotaxRpmMax = rotaxRpmMaxForOptions(options);
  if (!Number.isFinite(rotaxRpmMax) || rotaxRpmMax <= range.max) return range;
  return Object.freeze({
    ...range,
    max: rotaxRpmMax,
  });
}

function sensorJumpLimitForSample(type, range, dt, delta, options = {}) {
  if (type === "rpm" && delta > 0 && isRotaxEngineContext(options)) {
    const rotaxRise = sensorsParameters.rotaxRpmRiseJump;
    return Math.max(rotaxRise.floor, dt * rotaxRise.rate);
  }
  return Math.max(range.jumpFloor, dt * range.jumpRate);
}

function fuelFlowImplausiblyLowForThermalContext(point, range, key = "fuelFlow") {
  const value = point?.[key];
  if (!Number.isFinite(value) || value <= 0 || value > range.implausibleLowMaxGph) return null;
  const rpm = point?.rpm;
  const map = point?.map;
  const avgEgt = averagePointKeys(point, cylinderKeys("egt"));
  const avgCht = averagePointKeys(point, cylinderKeys("cht"));
  const rpmLoaded = Number.isFinite(rpm) && rpm >= range.implausibleLowMinRpm;
  const mapLoaded = Number.isFinite(map) && map >= range.implausibleLowMinMapInHg;
  const thermalHot = Number.isFinite(avgEgt) && avgEgt >= range.implausibleLowMinAvgEgtF
    && Number.isFinite(avgCht) && avgCht >= range.implausibleLowMinAvgChtF;
  if (!(rpmLoaded && mapLoaded && thermalHot)) return null;
  return {
    kind: "implausibleLowFuelFlow",
    value,
    peer: null,
    delta: null,
    rpm,
    map,
    avgEgt,
    avgCht,
  };
}

function rpmImplausiblyLowForThermalContext(point, range, key = "rpm") {
  const value = point?.[key];
  if (!Number.isFinite(value) || value > range.implausibleLowMaxRpm) return null;
  const map = point?.map;
  const fuelFlow = point?.fuelFlow;
  const avgEgt = averagePointKeys(point, cylinderKeys("egt"));
  const avgCht = averagePointKeys(point, cylinderKeys("cht"));
  const manifoldLoaded = Number.isFinite(map) && map >= range.implausibleLowMinMapInHg;
  const fuelLoaded = Number.isFinite(fuelFlow) && fuelFlow >= range.implausibleLowMinFuelFlowGph;
  const thermalHot = Number.isFinite(avgEgt) && avgEgt >= range.implausibleLowMinAvgEgtF
    && Number.isFinite(avgCht) && avgCht >= range.implausibleLowMinAvgChtF;
  if (!(manifoldLoaded && fuelLoaded && thermalHot)) return null;
  return {
    kind: "implausibleLowRpm",
    value,
    peer: null,
    delta: null,
    map,
    fuelFlow,
    avgEgt,
    avgCht,
  };
}

function mapImplausiblyLowForThermalContext(point, range, key = "map") {
  const value = point?.[key];
  if (!Number.isFinite(value) || value > range.implausibleLowMaxInHg) return null;
  const rpm = point?.rpm;
  const fuelFlow = point?.fuelFlow;
  const avgEgt = averagePointKeys(point, cylinderKeys("egt"));
  const avgCht = averagePointKeys(point, cylinderKeys("cht"));
  const rpmLoaded = Number.isFinite(rpm) && rpm >= range.implausibleLowMinRpm;
  const fuelLoaded = Number.isFinite(fuelFlow) && fuelFlow >= range.implausibleLowMinFuelFlowGph;
  const thermalHot = Number.isFinite(avgEgt) && avgEgt >= range.implausibleLowMinAvgEgtF
    && Number.isFinite(avgCht) && avgCht >= range.implausibleLowMinAvgChtF;
  if (!(rpmLoaded && fuelLoaded && thermalHot)) return null;
  return {
    kind: "implausibleLowMap",
    value,
    peer: null,
    delta: null,
    rpm,
    fuelFlow,
    avgEgt,
    avgCht,
  };
}

function findSensorFlatline(rows, key, peerKeys, range) {
  let runStart = null;
  let previousValue = null;
  let best = null;
  const finishRun = (endIndex) => {
    if (runStart === null || endIndex <= runStart) return;
    const startPoint = rows[runStart];
    const endPoint = rows[endIndex];
    const duration = (endPoint.elapsedSeconds ?? endIndex) - (startPoint.elapsedSeconds ?? runStart);
    if (duration < range.flatlineSeconds) return;
    const startPeer = peerAverage(startPoint, peerKeys, key);
    const endPeer = peerAverage(endPoint, peerKeys, key);
    const peerMotion = Number.isFinite(startPeer) && Number.isFinite(endPeer) ? Math.abs(endPeer - startPeer) : null;
    if (!Number.isFinite(peerMotion) || peerMotion < range.flatlinePeerMotion) return;
    const candidate = {
      value: previousValue,
      duration,
      peerMotion,
      startTime: startPoint.elapsedSeconds ?? runStart,
      endTime: endPoint.elapsedSeconds ?? endIndex,
    };
    if (!best || candidate.duration > best.duration) best = candidate;
  };

  rows.forEach((point, index) => {
    const value = point[key];
    if (!Number.isFinite(value)) {
      finishRun(index - 1);
      runStart = null;
      previousValue = null;
      return;
    }
    if (value !== previousValue) {
      finishRun(index - 1);
      runStart = index;
      previousValue = value;
    }
  });
  finishRun(rows.length - 1);
  return best;
}

function sensorPointCondition(point, key, peerKeys, range) {
  const value = point?.[key];
  if (!Number.isFinite(value)) return null;
  const type = sensorChannelType(key);
  if (type === "rpm") {
    const implausibleLow = rpmImplausiblyLowForThermalContext(point, range, key);
    if (implausibleLow) return implausibleLow;
  }
  if (type === "map") {
    const implausibleLow = mapImplausiblyLowForThermalContext(point, range, key);
    if (implausibleLow) return implausibleLow;
  }
  if (type === "fuelFlow") {
    const implausibleLow = fuelFlowImplausiblyLowForThermalContext(point, range, key);
    if (implausibleLow) return implausibleLow;
  }
  const peer = peerAverage(point, peerKeys, key);
  const peerIsComparable = Number.isFinite(peer) && peer >= range.peerMinimum;
  if (value <= 0) {
    return { kind: "dropout", value, peer, delta: peerIsComparable ? value - peer : null };
  }
  if (value < range.min || value > range.max) {
    const limit = value < range.min ? range.min : range.max;
    return {
      kind: "range",
      value,
      peer,
      delta: peerIsComparable ? value - peer : null,
      rangeMin: range.min,
      rangeMax: range.max,
      limit,
      limitDirection: value < range.min ? "low" : "high",
      exceedAmount: Math.abs(value - limit),
    };
  }
  if (!peerIsComparable) return null;
  const delta = value - peer;
  if (value <= peer - range.coldPeerDelta) return { kind: "coldPeer", value, peer, delta };
  if (value >= peer + range.hotPeerDelta) return { kind: "hotPeer", value, peer, delta };
  return null;
}

function sensorConditionReason(kind) {
  return {
    coldPeer: "cold versus peers",
    hotPeer: "hot versus peers",
    dropout: "dropout",
    range: "out of range",
    flatline: "flatline",
    noise: "noisy / high jitter",
    jumps: "repeated abrupt jumps",
    implausibleLowRpm: "implausibly low for engine heat",
    implausibleLowMap: "implausibly low for engine heat",
    implausibleLowFuelFlow: "implausibly low for engine heat",
  }[kind] ?? "signal review";
}

function findSensorJumpWindows(jumpEvents, range) {
  const gapLimit = range.conditionGapSeconds ?? 12;
  const minimumDuration = range.conditionSeconds ?? 90;
  const minimumCount = range.jumpWindowMinimum ?? 6;
  const windows = [];
  let run = null;

  const finishRun = () => {
    if (!run) return;
    const duration = run.endTime - run.startTime;
    if (run.count >= minimumCount && duration >= minimumDuration) {
      windows.push({
        startTime: run.startTime,
        endTime: run.endTime,
        duration,
        kind: "jumps",
        reason: sensorConditionReason("jumps"),
        affectedCount: run.count,
        totalCount: run.count,
        maxDelta: run.maxDelta,
        label: "TIT signal quality review",
      });
    }
    run = null;
  };

  jumpEvents.forEach((event) => {
    if (run && event.time - run.endTime > gapLimit) finishRun();
    if (!run) {
      run = {
        startTime: event.time,
        endTime: event.time,
        count: 0,
        maxDelta: null,
      };
    }
    run.endTime = event.time;
    run.count += 1;
    run.maxDelta = Number.isFinite(run.maxDelta) ? Math.max(run.maxDelta, event.unexplainedDelta) : event.unexplainedDelta;
  });
  finishRun();
  return windows.sort((a, b) => b.duration - a.duration || b.affectedCount - a.affectedCount);
}

function findSensorConditionWindows(rows, key, peerKeys, range) {
  const minimumDuration = range.conditionSeconds ?? 90;
  const gapLimit = range.conditionGapSeconds ?? 12;
  const minimumAffectedRatio = range.conditionAffectedRatio ?? 0.55;
  const windows = [];
  let run = null;

  const finishRun = () => {
    if (!run) return;
    const duration = run.lastIssueTime - run.startTime;
    const affectedRatio = run.totalCount ? run.affectedCount / run.totalCount : 0;
    if (duration >= minimumDuration && affectedRatio >= minimumAffectedRatio) {
      const [kind] = Object.entries(run.kindCounts)
        .sort((a, b) => b[1] - a[1])[0] ?? ["signal"];
      windows.push({
        startTime: run.startTime,
        endTime: run.lastIssueTime,
        duration,
        kind,
        reason: sensorConditionReason(kind),
        affectedCount: run.affectedCount,
        totalCount: run.totalCount,
        affectedRatio,
        minDelta: run.minDelta,
        maxDelta: run.maxDelta,
        minValue: run.minValue,
        maxValue: run.maxValue,
        minValueTime: run.minValueTime,
        maxValueTime: run.maxValueTime,
        rangeMin: run.rangeMin,
        rangeMax: run.rangeMax,
        outOfRangeLowCount: run.outOfRangeLowCount,
        outOfRangeHighCount: run.outOfRangeHighCount,
        worstLowValue: run.worstLowValue,
        worstLowTime: run.worstLowTime,
        worstHighValue: run.worstHighValue,
        worstHighTime: run.worstHighTime,
        implausibleLowCount: run.implausibleLowCount,
        implausibleLowFuelFlowCount: run.implausibleLowFuelFlowCount,
      });
    }
    run = null;
  };

  const startRun = (point, index) => {
    const time = point.elapsedSeconds ?? index;
    run = {
      startTime: time,
      lastIssueTime: time,
      affectedCount: 0,
      totalCount: 0,
      kindCounts: {},
      minDelta: null,
      maxDelta: null,
      minValue: null,
      maxValue: null,
      minValueTime: null,
      maxValueTime: null,
      rangeMin: range.min,
      rangeMax: range.max,
      outOfRangeLowCount: 0,
      outOfRangeHighCount: 0,
      worstLowValue: null,
      worstLowTime: null,
      worstHighValue: null,
      worstHighTime: null,
      implausibleLowCount: 0,
      implausibleLowFuelFlowCount: 0,
    };
  };

  const addCondition = (point, index, condition) => {
    const time = point.elapsedSeconds ?? index;
    run.lastIssueTime = time;
    run.affectedCount += 1;
    run.totalCount += 1;
    run.kindCounts[condition.kind] = (run.kindCounts[condition.kind] ?? 0) + 1;
    if (Number.isFinite(condition.delta)) {
      run.minDelta = Number.isFinite(run.minDelta) ? Math.min(run.minDelta, condition.delta) : condition.delta;
      run.maxDelta = Number.isFinite(run.maxDelta) ? Math.max(run.maxDelta, condition.delta) : condition.delta;
    }
    if (Number.isFinite(condition.value)) {
      if (!Number.isFinite(run.minValue) || condition.value < run.minValue) {
        run.minValue = condition.value;
        run.minValueTime = time;
      }
      if (!Number.isFinite(run.maxValue) || condition.value > run.maxValue) {
        run.maxValue = condition.value;
        run.maxValueTime = time;
      }
    }
    if (condition.kind === "range") {
      if (condition.limitDirection === "low") {
        run.outOfRangeLowCount += 1;
        if (!Number.isFinite(run.worstLowValue) || condition.value < run.worstLowValue) {
          run.worstLowValue = condition.value;
          run.worstLowTime = time;
        }
      } else if (condition.limitDirection === "high") {
        run.outOfRangeHighCount += 1;
        if (!Number.isFinite(run.worstHighValue) || condition.value > run.worstHighValue) {
          run.worstHighValue = condition.value;
          run.worstHighTime = time;
        }
      }
    } else if (/^implausibleLow(?:Rpm|Map|FuelFlow)$/.test(condition.kind)) {
      run.implausibleLowCount += 1;
      if (condition.kind === "implausibleLowFuelFlow") run.implausibleLowFuelFlowCount += 1;
    }
  };

  rows.forEach((point, index) => {
    const time = point.elapsedSeconds ?? index;
    const condition = sensorPointCondition(point, key, peerKeys, range);
    if (condition) {
      if (run && time - run.lastIssueTime > gapLimit) finishRun();
      if (!run) startRun(point, index);
      addCondition(point, index, condition);
      return;
    }
    if (!run) return;
    if (time - run.lastIssueTime > gapLimit) {
      finishRun();
      return;
    }
    run.totalCount += 1;
  });

  finishRun();
  return windows.sort((a, b) => b.duration - a.duration);
}

function averageFinite(values) {
  const finite = values.filter(Number.isFinite);
  if (!finite.length) return null;
  return finite.reduce((sum, value) => sum + value, 0) / finite.length;
}

function averagePointKeys(point, keys) {
  return averageFinite(keys.map((key) => point?.[key]));
}

function fuelFlowJumpHasThermalResponse(rows, index, delta) {
  const config = sensorsParameters.fuelFlowThermalResponse;
  if (!Number.isFinite(delta) || Math.abs(delta) < config.minFuelFlowDeltaGph) return false;
  const egtKeys = cylinderKeys("egt").filter((key) => rows.some((point) => Number.isFinite(point[key])));
  if (egtKeys.length < config.minEgtKeys) return false;

  const point = rows[index];
  const time = point?.elapsedSeconds ?? index;
  const before = rows.filter((candidate, candidateIndex) => {
    const candidateTime = candidate.elapsedSeconds ?? candidateIndex;
    return candidateTime >= time - config.beforeSeconds && candidateTime < time;
  });
  const after = rows.filter((candidate, candidateIndex) => {
    const candidateTime = candidate.elapsedSeconds ?? candidateIndex;
    return candidateTime > time && candidateTime <= time + config.afterSeconds;
  });
  const beforeEgt = averageFinite(before.map((candidate) => averagePointKeys(candidate, egtKeys)));
  const afterEgt = averageFinite(after.map((candidate) => averagePointKeys(candidate, egtKeys)));
  if (!Number.isFinite(beforeEgt) || !Number.isFinite(afterEgt)) return false;

  const egtDelta = afterEgt - beforeEgt;
  if (Math.abs(egtDelta) < config.minEgtDeltaF) return false;
  return Math.sign(egtDelta) === Math.sign(delta);
}

// Restrict noise measurement to genuinely steady CONTIGUOUS cruise segments.
// Three lessons from live false positives are encoded here:
//   1. Cruise phase ONLY, no fallback — a ground-run/taxi flight has no steady
//      state, and throttle jockeying read as "noise" (a ground-run-only flight).
//   2. Rows where the bank median moves fast are engine transients (leaning and
//      GAMI sweeps, mag checks, power steps) — commanded changes move the whole
//      bank together within seconds, while probe noise is zero-mean and mostly
//      cancels in the bank median. Sweeps measured 15 degF "RMS" before this
//      mask (a cruise mixture-sweep flight).
//   3. Residuals must never span a seam: a masked-out stretch or a gap between
//      cruise segments hides a legitimate level change that the rolling median
//      then reads as jitter (a multi-segment cruise flight). Rows are split into
//      contiguous runs and each run is measured on its own.
// No qualifying steady cruise means NO noise verdict — signal quality cannot be
// judged while the engine state is moving.
function sensorNoiseSteadySegments(rows, type, noiseParams, typeConfig, engineBankKeys = null) {
  const cruise = rows.filter((point) => String(point.phase ?? "") === "Cruise");
  if (!cruise.length) return [];
  const rpms = cruise.map((point) => point.rpm).filter(Number.isFinite);
  let masked = cruise;
  if (rpms.length >= noiseParams.minSteadySamples) {
    const medianRpm = median(rpms);
    const tolerance = Math.max(noiseParams.steadyRpmFloorRpm, medianRpm * noiseParams.steadyRpmTolerance);
    masked = cruise.filter((point) => !Number.isFinite(point.rpm) || Math.abs(point.rpm - medianRpm) <= tolerance);
  }

  // Restrict the maneuver-mask bank median to THIS probe's engine on twins, so a
  // single-engine sweep still moves its own bank median (a 12-probe cross-engine
  // median barely moves for a 6-probe swing) and gets excluded.
  const candidateKeys = engineBankKeys?.length ? engineBankKeys : cylinderKeys(type);
  const bankKeys = candidateKeys.filter((key) => masked.some((point) => Number.isFinite(point[key])));
  const bank = masked.map((point) => median(bankKeys.map((key) => point[key])));
  const span = noiseParams.window;
  const keep = masked.map((_, index) => {
    const before = bank[Math.max(0, index - span)];
    const after = bank[Math.min(bank.length - 1, index + span)];
    if (!Number.isFinite(before) || !Number.isFinite(after)) return true;
    return Math.abs(after - before) <= typeConfig.maneuverSlewF;
  });

  const step = rollingMedianStep(masked);
  const maxGap = Math.max(noiseParams.maxSegmentGapSeconds, step * 2.5);
  const segments = [];
  let current = [];
  let previousTime = null;
  masked.forEach((point, index) => {
    if (!keep[index]) {
      if (current.length) segments.push(current);
      current = [];
      previousTime = null;
      return;
    }
    const time = point.elapsedSeconds ?? index;
    if (previousTime !== null && time - previousTime > maxGap) {
      if (current.length) segments.push(current);
      current = [];
    }
    current.push(point);
    previousTime = time;
  });
  if (current.length) segments.push(current);
  return segments.filter((segment) => segment.length >= noiseParams.minSegmentSamples);
}

// High-frequency residual: subtract a short centered rolling median (so slow
// real trends are removed) and measure what remains, in the channel's own
// units. This is self-referential — NOT peer-subtracted — so common-mode noise
// that the jump path cancels is still measured here. Returns raw sums so one
// channel's stats can aggregate across several contiguous steady segments.
function residualStats(values, window, floor) {
  const count = values.length;
  const half = Math.floor(window / 2);
  if (count < window) return null;
  let sumSquares = 0;
  let residualCount = 0;
  let activeCount = 0;
  for (let index = half; index < count - half; index += 1) {
    const segment = values.slice(index - half, index + half + 1).sort((a, b) => a - b);
    const residual = values[index] - segment[half];
    sumSquares += residual * residual;
    residualCount += 1;
    if (Math.abs(residual) > floor) activeCount += 1;
  }
  if (!residualCount) return null;
  return { sumSquares, residualCount, activeCount };
}

function sensorChannelNoise(rows, key, type, context = {}) {
  const noiseParams = sensorsParameters.noise;
  const config = noiseParams.perType[type];
  if (!config) return null;
  // Slow loggers (JPI/EDM/MVP at 1-6s) carry no sub-second content, so a "noise"
  // reading would just be quantization or decimation. Only score fast loggers.
  const sampleInterval = Number.isFinite(context.sampleIntervalSeconds) ? context.sampleIntervalSeconds : rollingMedianStep(rows);
  if (sampleInterval > noiseParams.maxSampleIntervalSeconds) return null;
  const range = sensorRangeForType(type);
  // Steady segments are a per-bank property (cruise + rpm + maneuver masking, gap
  // splitting) — NOT per-probe — so memoize them by bank: a 6-cyl EGT bank builds
  // them once, not once per probe. The per-channel residual loop below reads its
  // own key out of the shared segments.
  const bank = context.banks?.get(key) ?? null;
  const memoKey = bank?.bankId ?? type;
  let segments = context.segmentCache?.get(memoKey);
  if (!segments) {
    segments = sensorNoiseSteadySegments(rows, type, noiseParams, config, bank?.bankKeys ?? null);
    context.segmentCache?.set(memoKey, segments);
  }
  let sumSquares = 0;
  let residualCount = 0;
  let activeCount = 0;
  let sampleCount = 0;
  let startTime = null;
  let endTime = null;
  segments.forEach((segment) => {
    const series = [];
    let segStart = null;
    let segEnd = null;
    segment.forEach((point) => {
      const value = point[key];
      // Dropouts and out-of-range samples are scored elsewhere; letting them into
      // the residual squares a full-scale excursion into fake "jitter" (a single 0
      // reads as ~1300 degF), so exclude them from the signal-quality measurement.
      if (!Number.isFinite(value) || value <= 0 || value < range.min || value > range.max) return;
      series.push(value);
      const time = point.elapsedSeconds;
      if (Number.isFinite(time)) {
        if (segStart === null) segStart = time;
        segEnd = time;
      }
    });
    const stats = residualStats(series, noiseParams.window, noiseParams.residualFloor);
    if (!stats) return;
    sumSquares += stats.sumSquares;
    residualCount += stats.residualCount;
    activeCount += stats.activeCount;
    sampleCount += series.length;
    // Only measured segments define the window, in absolute time, so it never
    // spans an excluded/unmeasured stretch and endTime never precedes startTime.
    if (Number.isFinite(segStart)) {
      if (startTime === null || segStart < startTime) startTime = segStart;
      if (endTime === null || segEnd > endTime) endTime = segEnd;
    }
  });
  if (sampleCount < noiseParams.minSteadySamples || !residualCount) return null;
  return {
    rms: Math.sqrt(sumSquares / residualCount),
    density: activeCount / residualCount,
    sampleCount,
    startTime,
    endTime,
  };
}

export function summarizeSensorChannel(rows, key, peerKeys, options = {}) {
  const type = sensorChannelType(key);
  const range = sensorRangeForKey(key, options);
  let finiteCount = 0;
  let zeroCount = 0;
  let outOfRangeCount = 0;
  let coldMismatchCount = 0;
  let hotMismatchCount = 0;
  let implausibleLowCount = 0;
  let impossibleJumpCount = 0;
  let peerComparableCount = 0;
  let minValue = null;
  let maxValue = null;
  let minValueTime = null;
  let maxValueTime = null;
  let outOfRangeLowCount = 0;
  let outOfRangeHighCount = 0;
  let worstLowValue = null;
  let worstLowTime = null;
  let worstHighValue = null;
  let worstHighTime = null;
  let previousPoint = null;
  let previousValue = null;
  const jumpEvents = [];
  let engineCorrelatedJumpCount = 0;
  let firstTime = null;
  let lastTime = null;

  rows.forEach((point, index) => {
    const value = point[key];
    if (!Number.isFinite(value)) return;
    finiteCount += 1;
    if (firstTime === null) firstTime = point.elapsedSeconds ?? index;
    lastTime = point.elapsedSeconds ?? index;
    const time = point.elapsedSeconds ?? index;
    if (!Number.isFinite(minValue) || value < minValue) {
      minValue = value;
      minValueTime = time;
    }
    if (!Number.isFinite(maxValue) || value > maxValue) {
      maxValue = value;
      maxValueTime = time;
    }
    if (value <= 0) zeroCount += 1;
    if (
      (type === "rpm" && rpmImplausiblyLowForThermalContext(point, range, key))
      || (type === "map" && mapImplausiblyLowForThermalContext(point, range, key))
      || (type === "fuelFlow" && fuelFlowImplausiblyLowForThermalContext(point, range, key))
    ) {
      implausibleLowCount += 1;
    }
    if (value < range.min || value > range.max) {
      outOfRangeCount += 1;
      if (value < range.min) {
        outOfRangeLowCount += 1;
        if (!Number.isFinite(worstLowValue) || value < worstLowValue) {
          worstLowValue = value;
          worstLowTime = time;
        }
      } else {
        outOfRangeHighCount += 1;
        if (!Number.isFinite(worstHighValue) || value > worstHighValue) {
          worstHighValue = value;
          worstHighTime = time;
        }
      }
    }

    const peer = peerAverage(point, peerKeys, key);
    if (Number.isFinite(peer) && peer >= range.peerMinimum) {
      peerComparableCount += 1;
      if (value <= peer - range.coldPeerDelta) coldMismatchCount += 1;
      if (value >= peer + range.hotPeerDelta) hotMismatchCount += 1;
    }

    if (previousPoint && Number.isFinite(previousValue)) {
      const dt = Math.max(0.2, (point.elapsedSeconds ?? index) - (previousPoint.elapsedSeconds ?? index - 1));
      const delta = value - previousValue;
      const peerNow = peerAverage(point, peerKeys, key);
      const peerBefore = peerAverage(previousPoint, peerKeys, key);
      const peerDelta = Number.isFinite(peerNow) && Number.isFinite(peerBefore) ? peerNow - peerBefore : 0;
      const unexplainedDelta = Math.abs(delta - peerDelta);
      if (unexplainedDelta > sensorJumpLimitForSample(type, range, dt, delta, options)) {
        if (type === "fuelFlow" && fuelFlowJumpHasThermalResponse(rows, index, delta)) {
          engineCorrelatedJumpCount += 1;
          previousPoint = point;
          previousValue = value;
          return;
        }
        impossibleJumpCount += 1;
        jumpEvents.push({
          time: point.elapsedSeconds ?? index,
          delta,
          unexplainedDelta,
        });
      }
    }

    previousPoint = point;
    previousValue = value;
  });

  const totalRows = rows.length || 1;
  const missingRatio = Math.max(0, totalRows - finiteCount) / totalRows;
  const zeroRatio = finiteCount ? zeroCount / finiteCount : 0;
  const outOfRangeRatio = finiteCount ? outOfRangeCount / finiteCount : 0;
  const coldMismatchRatio = peerComparableCount ? coldMismatchCount / peerComparableCount : 0;
  const hotMismatchRatio = peerComparableCount ? hotMismatchCount / peerComparableCount : 0;
  const jumpRatio = finiteCount > 1 ? impossibleJumpCount / (finiteCount - 1) : 0;
  const flatline = findSensorFlatline(rows, key, peerKeys, range);
  const jumpWindows = findSensorJumpWindows(jumpEvents, range);
  const jumpWindow = jumpWindows[0] ?? null;
  const conditionWindows = [
    ...findSensorConditionWindows(rows, key, peerKeys, range),
    ...jumpWindows,
  ].sort((a, b) => (b.duration ?? 0) - (a.duration ?? 0));
  const conditionWindow = conditionWindows[0] ?? null;
  const jumpConfig = sensorsParameters.jumpSuspicion;
  const weights = sensorsParameters.suspicionWeights;
  const jumpSuspicion = Math.min(
    jumpConfig.cap,
    impossibleJumpCount * jumpConfig.countWeight
      + jumpRatio * jumpConfig.ratioWeight
      + (jumpWindow ? Math.min(jumpConfig.windowCap, jumpConfig.windowBase + jumpWindow.duration / jumpConfig.windowDurationDivisor) : 0),
  );
  const powerSignalJumpPairConfig = sensorsParameters.powerSignalJumpPair;
  const powerSignalJumpPairSuspicion = (type === "rpm" || type === "map") && impossibleJumpCount >= powerSignalJumpPairConfig.minimumCount
    ? powerSignalJumpPairConfig.suspicion
    : 0;
  const implausibleFuelLowSuspicion = conditionWindows
    .filter((window) => window.kind === "implausibleLowFuelFlow")
    .reduce((total, window) => total + Math.min(
      weights.implausibleFuelLowCap,
      weights.implausibleFuelLowBase + window.duration / weights.implausibleFuelLowDurationDivisor,
    ), 0);
  const implausiblePowerLowSuspicion = conditionWindows
    .filter((window) => window.kind === "implausibleLowRpm" || window.kind === "implausibleLowMap")
    .reduce((total, window) => total + Math.min(
      weights.implausiblePowerLowCap,
      weights.implausiblePowerLowBase + window.duration / weights.implausiblePowerLowDurationDivisor,
    ), 0);
  const suspicion = zeroRatio * weights.zeroRatio
    + outOfRangeRatio * weights.outOfRangeRatio
    + missingRatio * weights.missingRatio
    + coldMismatchRatio * weights.coldMismatchRatio
    + hotMismatchRatio * weights.hotMismatchRatio
    + jumpSuspicion
    + powerSignalJumpPairSuspicion
    + implausibleFuelLowSuspicion
    + implausiblePowerLowSuspicion
    + (conditionWindow ? Math.min(weights.conditionWindowCap, weights.conditionWindowBase + conditionWindow.duration / weights.conditionWindowDurationDivisor) : 0)
    + (flatline ? Math.min(weights.flatlineCap, weights.flatlineBase + flatline.duration / weights.flatlineDurationDivisor) : 0);

  // Only analyzeSensorValidity asks for the noise measurement; the EGT-balance and
  // valve detectors also call summarizeSensorChannel but never read the noise
  // fields, so skip the whole steady-segment pipeline for them.
  const noise = options.measureNoise ? sensorChannelNoise(rows, key, type, options.noiseContext) : null;

  return {
    key,
    label: labelForSensorKey(key, options),
    type,
    finiteCount,
    missingCount: Math.max(0, totalRows - finiteCount),
    missingRatio,
    rangeMin: range.min,
    rangeMax: range.max,
    minValue,
    maxValue,
    minValueTime,
    maxValueTime,
    zeroCount,
    outOfRangeCount,
    outOfRangeLowCount,
    outOfRangeHighCount,
    worstLowValue,
    worstLowTime,
    worstHighValue,
    worstHighTime,
    coldMismatchCount,
    hotMismatchCount,
    implausibleLowCount,
    implausibleLowFuelFlowCount: type === "fuelFlow" ? implausibleLowCount : 0,
    implausibleLowRpmCount: type === "rpm" ? implausibleLowCount : 0,
    implausibleLowMapCount: type === "map" ? implausibleLowCount : 0,
    peerComparableCount,
    coldMismatchRatio,
    hotMismatchRatio,
    impossibleJumpCount,
    powerSignalJumpPairSuspicion,
    engineCorrelatedJumpCount,
    jumpRatio,
    jumpEvents,
    jumpWindows,
    jumpWindow,
    flatline,
    conditionWindows,
    conditionWindow,
    firstTime,
    lastTime,
    noiseRms: noise?.rms ?? null,
    noiseDensity: noise?.density ?? null,
    noiseSampleCount: noise?.sampleCount ?? 0,
    noiseWindow: noise ? { startTime: noise.startTime, endTime: noise.endTime, sampleCount: noise.sampleCount } : null,
    suspicion,
  };
}

function sensorValidityNoteForTop(top, score) {
  if (score >= sensorsParameters.scoring.reviewScoreThreshold) return "Probe channels look stable";
  if (/^implausibleLow(?:Rpm|Map|FuelFlow)$/.test(top.conditionWindow?.kind ?? "")) return `${top.label} implausibly low for engine heat`;
  // Noise is checked before zero/flatline (matching the tile note in
  // health-note-markup) so the two surfaces agree; the kind === "noise" guard means
  // a longer real dropout/flatline window still wins and falls through below.
  if (top.noiseSuspicion > 0 && top.conditionWindow?.kind === "noise") {
    if (top.noiseAffectedCount > 1) {
      const bank = top.type === "egt" ? "EGT" : top.type === "cht" ? "CHT" : top.label;
      return `${bank} probes reading noisy`;
    }
    return `${top.label} signal reading noisy`;
  }
  if (top.zeroCount) return `${top.label} has zero/dropout samples`;
  if (top.flatline) return `${top.label} flatline pattern`;
  if (top.jumpWindow) return `${top.label} signal has repeated jumps`;
  if (top.conditionWindow) return `${top.label} signal affects ${formatElapsedTime(top.conditionWindow.duration)}`;
  if (top.impossibleJumpCount) return `${top.label} has abrupt data jumps`;
  if (top.coldMismatchCount || top.hotMismatchCount) return `${top.label} split from peer probes`;
  return `${top.label} probe pattern needs review`;
}

function coolingRateProbeCueFromEvent(coolingSummary, event, rateFpm, peerMax) {
  if (!event || !Number.isFinite(event.startTime) || !Number.isFinite(event.endTime)) return null;
  return {
    kind: "coolingRateStep",
    reason: "abrupt cooling-rate step",
    startTime: Number.isFinite(event.reviewStartTime) ? event.reviewStartTime : event.startTime,
    endTime: Number.isFinite(event.reviewEndTime) ? event.reviewEndTime : event.endTime,
    reviewStartTime: Number.isFinite(event.reviewStartTime) ? event.reviewStartTime : null,
    reviewEndTime: Number.isFinite(event.reviewEndTime) ? event.reviewEndTime : null,
    eventStartTime: event.startTime,
    eventEndTime: event.endTime,
    eventDuration: event.durationSeconds ?? Math.max(0, event.endTime - event.startTime),
    duration: Math.max(0, (Number.isFinite(event.reviewEndTime) ? event.reviewEndTime : event.endTime) - (Number.isFinite(event.reviewStartTime) ? event.reviewStartTime : event.startTime)),
    rateFpm,
    drop: event.drop,
    startCht: event.startCht,
    endCht: event.endCht,
    peerMaxRateFpm: peerMax,
    affectedCount: Math.max(1, event.count ?? coolingSummary.events?.length ?? 1),
    totalCount: Math.max(1, coolingSummary.sampleCount ?? 1),
  };
}

function strongestCoolingRateProbeEvents(events = [], limit = sensorsParameters.coolingRateProbeCue.maxWindowsPerChannel) {
  const selected = [];
  events
    .filter((event) => Number.isFinite(event?.startTime) && Number.isFinite(event?.endTime) && Number.isFinite(event?.rateFpm))
    .sort((a, b) => b.rateFpm - a.rateFpm)
    .forEach((event) => {
      const overlapsSelected = selected.some((candidate) => (
        event.startTime <= candidate.endTime + 20
        && candidate.startTime <= event.endTime + 20
      ));
      if (!overlapsSelected) selected.push(event);
    });
  return selected.slice(0, limit);
}

function coolingRateProbeCuesForSensorSummary(coolingSummary, coolingSummaries, thresholdFpm = chtCoolingRateTriggerFpm) {
  if (!coolingSummary?.key || sensorChannelType(coolingSummary.key) !== "cht") return [];
  const config = sensorsParameters.coolingRateProbeCue;
  const hardProbeRateFpm = Math.max(config.minimumHardRateFpm, thresholdFpm * config.triggerMultiplier);

  const peerRates = coolingSummaries
    .filter((summary) => summary?.key !== coolingSummary.key)
    .map((summary) => summary?.maxRateFpm ?? summary?.top?.rateFpm)
    .filter(Number.isFinite);
  const peerMax = extentFromValues(peerRates)?.max ?? null;
  const events = strongestCoolingRateProbeEvents(coolingSummary.events ?? []);
  if (!events.length && coolingSummary.top) events.push(coolingSummary.top);
  return events
    .map((event) => {
      const rateFpm = event?.rateFpm ?? coolingSummary.maxRateFpm;
      if (!Number.isFinite(rateFpm) || rateFpm < hardProbeRateFpm) return null;
      const isolatedFromPeers = !Number.isFinite(peerMax)
        || rateFpm >= Math.max(peerMax * config.peerRateMultiplier, peerMax + config.peerRateDeltaFpm);
      if (!isolatedFromPeers && rateFpm < hardProbeRateFpm * config.isolatedFallbackMultiplier) return null;
      return coolingRateProbeCueFromEvent(coolingSummary, event, rateFpm, peerMax);
    })
    .filter(Boolean)
    .sort((a, b) => (b.rateFpm ?? 0) - (a.rateFpm ?? 0))
    .slice(0, config.maxWindowsPerChannel);
}

export function augmentSensorValidityWithCoolingRate(sensorMetric, coolingRateMetric) {
  const sensorSummaries = sensorMetric?.details?.summaries;
  const coolingSummaries = coolingRateMetric?.details?.summaries;
  if (!Array.isArray(sensorSummaries) || !Array.isArray(coolingSummaries)) return sensorMetric;
  const thresholdFpm = Number.isFinite(Number(coolingRateMetric?.details?.thresholdFpm))
    ? Number(coolingRateMetric.details.thresholdFpm)
    : chtCoolingRateTriggerFpm;

  const cues = new Map(coolingSummaries
    .map((summary) => [summary?.key, coolingRateProbeCuesForSensorSummary(summary, coolingSummaries, thresholdFpm)])
    .filter(([, cueList]) => cueList?.length));
  if (!cues.size) return sensorMetric;

  const summaries = sensorSummaries.map((summary) => {
    const cueList = cues.get(summary.key);
    if (!cueList?.length) return summary;
    const config = sensorsParameters.coolingRateProbeCue;
    const hardRate = Math.max(config.minimumHardRateFpm, thresholdFpm * config.triggerMultiplier);
    const cue = cueList[0];
    const coolingSuspicion = Math.min(config.suspicionCap, config.suspicionBase + Math.max(0, cue.rateFpm - hardRate) / config.suspicionRateDivisor);
    return {
      ...summary,
      suspicion: Math.max(summary.suspicion ?? 0, coolingSuspicion),
      coolingRateProbeCue: cue,
      coolingRateProbeCues: cueList,
      conditionWindows: [
        ...(summary.conditionWindows ?? []),
        ...cueList,
      ].sort((a, b) => (b.duration ?? 0) - (a.duration ?? 0)),
      conditionWindow: [cue, summary.conditionWindow].filter(Boolean)
        .sort((a, b) => (b.duration ?? 0) - (a.duration ?? 0))[0] ?? null,
    };
  }).sort((a, b) => (b.suspicion ?? 0) - (a.suspicion ?? 0));

  const top = summaries[0];
  const score = top?.suspicion > 0
    ? clampScore(100 - Math.min(sensorsParameters.scoring.suspicionCap, top.suspicion))
    : sensorsParameters.scoring.cleanScore;
  return {
    ...sensorMetric,
    score,
    note: sensorValidityNoteForTop(top, score),
    details: {
      ...sensorMetric.details,
      top,
      summaries,
      coolingRateProbeCueKeys: [...cues.keys()],
    },
  };
}

// Common-mode noise (every probe of a bank jittering together) points to a
// shared ground/bus/wiring fault, not one probe. Fire only when >= minAffected
// same-type channels are noisy: grade by the median RMS of the affected probes
// (so a partially-noisy bank dings less than a fully-noisy one), nudge the worst
// probe to the top so it gets named, and let a single extreme probe cross into
// red on its own. Folds into each affected channel's `suspicion` so the tile
// score, note, findings, and swimlane all follow with no extra plumbing.
// Map each thermal probe to its engine-scoped bank (twins only). Returns null for
// single-engine aircraft so the global type grouping / bank median are unchanged.
// Without this, cylinderKeys(type) spans BOTH engines, so one bad probe per engine
// would trip a systemic "shared ground" verdict that cannot exist across engines.
function buildNoiseBanks(engineDescriptors) {
  if (!Array.isArray(engineDescriptors) || engineDescriptors.length <= 1) return null;
  const banks = new Map();
  engineDescriptors.forEach((engine, index) => {
    ["egt", "cht"].forEach((prefix) => {
      const bankKeys = (engine?.cylinders ?? []).map((cylinder) => cylinder?.[`${prefix}Key`]).filter(Boolean);
      bankKeys.forEach((key) => banks.set(key, { bankKeys, bankId: `${prefix}:${index}` }));
    });
  });
  return banks.size ? banks : null;
}

function applyThermalNoise(summaries, noiseBanks = null) {
  const noiseParams = sensorsParameters.noise;
  const bankIdForKey = (summary) => noiseBanks?.get(summary.key)?.bankId ?? summary.type;
  const groups = new Map();
  summaries.forEach((summary) => {
    if (!noiseParams.perType[summary.type]) return;
    if (!Number.isFinite(summary.noiseRms)) return;
    const bankId = bankIdForKey(summary);
    if (!groups.has(bankId)) groups.set(bankId, { type: summary.type, members: [] });
    groups.get(bankId).members.push(summary);
  });

  const bumps = new Map();
  groups.forEach(({ type, members: group }) => {
    const config = noiseParams.perType[type];
    // Two-tier gate: strong probes (>= flagRms, >= minActiveRatio) TRIGGER the
    // bank; once triggered, membership uses the lower memberRms/memberActiveRatio
    // floors so the shared fault names the whole bank. Membership must not reuse
    // the trigger thresholds — a uniformly noisy bank hovering around the trigger
    // line would be split in two (an observed live case). Severity stays anchored to
    // the TRIGGER probes so sweeping in milder members never dilutes the score.
    const triggers = group.filter((summary) => summary.noiseRms >= config.flagRms
      && summary.noiseDensity >= noiseParams.minActiveRatio);
    if (triggers.length < noiseParams.minAffectedChannels) return;
    const affected = group.filter((summary) => summary.noiseRms >= config.memberRms
      && summary.noiseDensity >= noiseParams.memberActiveRatio);
    // True median (average the two middles): utils.median returns the upper-middle
    // value, which for the common 2-trigger case equals the WORST probe's RMS,
    // over-grading the bank and zeroing the tie-break so the mild probe sorts on top.
    const triggerRms = triggers.map((summary) => summary.noiseRms).sort((a, b) => a - b);
    const midIndex = Math.floor(triggerRms.length / 2);
    const severityRms = triggerRms.length % 2 === 0
      ? (triggerRms[midIndex - 1] + triggerRms[midIndex]) / 2
      : triggerRms[midIndex];
    const worstRms = Math.max(...group.map((summary) => summary.noiseRms));
    const systemicSuspicion = Math.min(config.cap, Math.max(0, config.base + (severityRms - config.flagRms) * config.perDegree));
    affected.forEach((summary) => {
      let suspicion = systemicSuspicion + Math.max(0, summary.noiseRms - severityRms) * noiseParams.tieBreakPerDegree;
      if (summary.noiseRms >= config.singleCylRedRms) {
        suspicion = Math.max(suspicion, config.singleCylRedSuspicion + (summary.noiseRms - config.singleCylRedRms) * config.perDegree);
      }
      bumps.set(summary.key, {
        suspicion: Math.min(noiseParams.suspicionCap, suspicion),
        severityRms,
        worstRms,
        affectedCount: affected.length,
      });
    });
  });

  if (!bumps.size) return summaries;
  return summaries.map((summary) => {
    const bump = bumps.get(summary.key);
    if (!bump) return summary;
    const window = summary.noiseWindow && Number.isFinite(summary.noiseWindow.startTime)
      ? {
        kind: "noise",
        reason: sensorConditionReason("noise"),
        startTime: summary.noiseWindow.startTime,
        endTime: summary.noiseWindow.endTime,
        duration: Math.max(0, (summary.noiseWindow.endTime ?? 0) - (summary.noiseWindow.startTime ?? 0)),
        rms: summary.noiseRms,
        systemicRms: bump.severityRms,
        worstRms: bump.worstRms,
        affectedCount: bump.affectedCount,
        totalCount: summary.noiseSampleCount,
      }
      : null;
    return {
      ...summary,
      suspicion: (summary.suspicion ?? 0) + bump.suspicion,
      noiseSuspicion: bump.suspicion,
      noiseSystemicRms: bump.severityRms,
      noiseWorstRms: bump.worstRms,
      noiseAffectedCount: bump.affectedCount,
      conditionWindows: window
        ? [...(summary.conditionWindows ?? []), window].sort((a, b) => (b.duration ?? 0) - (a.duration ?? 0))
        : summary.conditionWindows,
      conditionWindow: window
        ? [window, summary.conditionWindow].filter(Boolean).sort((a, b) => (b.duration ?? 0) - (a.duration ?? 0))[0]
        : summary.conditionWindow,
    };
  });
}

export function analyzeSensorValidity(data, options = {}) {
  const scopedEngine = options.engineDescriptors?.length === 1 && options.engineDescriptors[0]?.side !== "single"
    ? options.engineDescriptors[0] : null;
  const keys = sensorChannelKeys(data).filter((key) => !scopedEngine?.channels?.[key] || scopedEngine.channels[key] === key);
  if (!keys.length) {
    return { key: "sensors", score: null, note: "No sensor channels", details: {} };
  }

  const workRows = data.filter(engineWorkPoint);
  const rows = workRows.length >= sensorsParameters.minWorkRows ? workRows : data;
  const noiseBanks = buildNoiseBanks(options.engineDescriptors);
  const channelOptions = {
    ...options,
    measureNoise: true,
    noiseContext: {
      banks: noiseBanks,
      sampleIntervalSeconds: rollingMedianStep(rows), // same for every channel; compute once
      segmentCache: new Map(),                        // steady segments memoized per bank
    },
  };
  const summaries = applyThermalNoise(
    keys.map((key) => summarizeSensorChannel(rows, key, sensorPeerKeysForKey(key, keys), channelOptions)),
    noiseBanks,
  ).sort((a, b) => b.suspicion - a.suspicion);
  const top = summaries[0];
  const score = top?.suspicion > 0
    ? clampScore(100 - Math.min(sensorsParameters.scoring.suspicionCap, top.suspicion))
    : sensorsParameters.scoring.cleanScore;
  const note = sensorValidityNoteForTop(top, score);

  return {
    key: "sensors",
    score,
    note,
    details: {
      top,
      summaries,
      channelCount: keys.length,
      workSamples: workRows.length,
      rowsChecked: rows.length,
      usedEngineRunRows: workRows.length >= sensorsParameters.minWorkRows,
    },
  };
}
